import type { WorkspaceModule } from '../workspace.ts'

/**
 * As regras de janela de cada tela no Modo PC — F138.
 *
 * Decisões do dono e do plano aprovado, num lugar só, para a próxima tela que
 * precisar de uma delas ser uma linha aqui e não uma condição espalhada.
 */

/**
 * Quantas janelas cabem abertas ao mesmo tempo.
 *
 * Cada janela é uma tela montada. As minimizadas desligam efeitos e consultas
 * (`<Activity mode="hidden">`), mas continuam ocupando memória — e o alvo
 * declarado é um Celeron com 4 GB (Master Specification §27). Doze é mais do
 * que alguém consegue acompanhar numa tela de balcão, e é um teto que se mede.
 */
export const MAX_WINDOWS = 12

/**
 * Telas que só podem ter uma janela.
 *
 * - PDV: é quem recebe o leitor de código de barras e as teclas F1 a F10; dois
 *   PDVs abertos disputariam o bipe.
 * - Caixa: uma gaveta, uma sessão de caixa.
 * - Atendimento: cada tela abre uma conexão ao vivo com o servidor (ADR-0030),
 *   e o navegador tem poucas por endereço.
 * - Importações: um arquivo sendo processado por vez.
 */
const SINGLE_INSTANCE: ReadonlySet<WorkspaceModule> = new Set([
  'pdv',
  'caixa',
  'atendimento',
  'importar',
  'importar-clientes',
  'importar-fotos',
  'importar-nfe',
])

export const isSingleInstance = (module: WorkspaceModule | null): boolean =>
  module !== null && SINGLE_INSTANCE.has(module)

/**
 * Telas que abrem ocupando a área de trabalho.
 *
 * O PDV e o Atendimento são espaços de trabalho, não fichas: numa janela de
 * 1024 por 640 a lista de itens do PDV e a conversa do Atendimento ficam
 * apertadas justamente onde a pessoa passa o dia. O editor de etiqueta é uma
 * mesa de desenho (F129) e segue a mesma regra, e a fila de produção (F170) é
 * uma planilha larga.
 */
const OPENS_MAXIMIZED: ReadonlySet<WorkspaceModule> = new Set([
  'pdv',
  'atendimento',
  // F170: a fila de produção são as vinte colunas da planilha da loja.
  'producao',
])

export const opensMaximized = (
  module: WorkspaceModule | null,
  pathname: string
): boolean =>
  (module !== null && OPENS_MAXIMIZED.has(module)) ||
  pathname.startsWith('/modelo-de-etiqueta/')
